"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useGetUnfilledAndRequiredIssueFieldsMetadata = exports.useGetIsFieldRequiredByIssueTypeId = exports.useChangeStatusWhenRequiredFieldsAreHidden = void 0;
var _lodash = require("lodash");
var _react = require("react");
var _sdk = require("../../../sdk");
var _hooks = require("../../state/redux/hooks");
var _issueTypes = require("../../state/redux/ducks/issueTypes");
var _issueCustomFieldMappingHooks = require("./issueCustomFieldMappingHooks");
var _issueFieldOrdering = require("../data/issueFieldOrdering");
var _issueRequiredFields = require("../data/issueRequiredFields");
var _issueFieldSanitization = require("../data/issueFieldSanitization");
const isRequiredIssueFieldUnfilled = _ref => {
  var _issue$watchers;
  let {
    fieldId,
    issue
  } = _ref;
  switch (fieldId) {
    case _sdk.IssueStandardFieldIds.Description:
      return (0, _issueRequiredFields.getIsIssueValueEmpty)(issue.description);
    case _sdk.IssueStandardFieldIds.Assignee:
      return (0, _issueRequiredFields.getIsIssueValueEmpty)(issue.assignedTo);
    case _sdk.IssueStandardFieldIds.Watchers:
      return (0, _issueRequiredFields.getIsIssueValueEmpty)(issue.watchers) || !((_issue$watchers = issue.watchers) !== null && _issue$watchers !== void 0 && _issue$watchers.length);
    case _sdk.IssueStandardFieldIds.Location:
      return (0, _issueRequiredFields.getIsIssueValueEmpty)(issue.locationId);
    case _sdk.IssueStandardFieldIds.LocationDetails:
      return (0, _issueRequiredFields.getIsIssueValueEmpty)(issue.locationDetails);
    case _sdk.IssueStandardFieldIds.DueDate:
      return (0, _issueRequiredFields.getIsIssueValueEmpty)(issue.dueDate);
    case _sdk.IssueStandardFieldIds.StartDate:
      return (0, _issueRequiredFields.getIsIssueValueEmpty)(issue.startDate);
    case _sdk.IssueStandardFieldIds.RootCauseWithCategory:
      return (0, _issueRequiredFields.getIsIssueValueEmpty)(issue.rootCauseId);
    default:
      const customAttribute = issue.customAttributes.find(_ref2 => {
        let {
          attributeDefinitionId
        } = _ref2;
        return attributeDefinitionId === fieldId;
      });
      return (0, _issueRequiredFields.getIsIssueValueEmpty)(customAttribute === null || customAttribute === void 0 ? void 0 : customAttribute.value);
  }
};
const useGetUnfilledAndRequiredIssueFieldsMetadata = () => {
  const issueTypeEntitiesMap = (0, _hooks.useSelector)(_issueTypes.getIssueTypeEntitiesMap);
  const getCustomFieldMappingsByIssueTypeId = (0, _issueCustomFieldMappingHooks.useGetCustomFieldMappingsByIssueTypeId)();
  return (0, _react.useCallback)(_ref3 => {
    var _issueType$fieldsMeta;
    let {
      issueTypeId,
      issue
    } = _ref3;
    const issueType = issueTypeId ? issueTypeEntitiesMap[issueTypeId] : undefined;
    if ((0, _lodash.isNil)(issueType)) {
      return [];
    }
    const fieldsMetadata = (_issueType$fieldsMeta = issueType.fieldsMetadata) !== null && _issueType$fieldsMeta !== void 0 ? _issueType$fieldsMeta : [];
    const customFieldMappings = getCustomFieldMappingsByIssueTypeId(issueType.id);
    const customFieldMappingIds = customFieldMappings.map(mapping => mapping.attributeDefinitionId);
    const sanitizedFieldsMetadataList = (0, _issueFieldOrdering.getSanitizedFieldList)({
      fieldList: fieldsMetadata,
      customFieldIdsForType: customFieldMappingIds
    });
    return sanitizedFieldsMetadataList.filter(_ref4 => {
      let {
        id,
        required
      } = _ref4;
      return !!required && isRequiredIssueFieldUnfilled({
        fieldId: id,
        issue
      });
    });
  }, [getCustomFieldMappingsByIssueTypeId, issueTypeEntitiesMap]);
};
exports.useGetUnfilledAndRequiredIssueFieldsMetadata = useGetUnfilledAndRequiredIssueFieldsMetadata;
const useGetIsFieldRequiredByIssueTypeId = () => {
  const issueTypeEntitiesMap = (0, _hooks.useSelector)(_issueTypes.getIssueTypeEntitiesMap);
  return (0, _react.useCallback)(_ref5 => {
    var _issueType$fieldsMeta2;
    let {
      issueFieldId,
      issueTypeId
    } = _ref5;
    const issueType = issueTypeEntitiesMap[issueTypeId];
    const fieldsMetadata = (_issueType$fieldsMeta2 = issueType === null || issueType === void 0 ? void 0 : issueType.fieldsMetadata) !== null && _issueType$fieldsMeta2 !== void 0 ? _issueType$fieldsMeta2 : [];
    return fieldsMetadata.some(_ref6 => {
      let {
        id,
        required
      } = _ref6;
      return id === issueFieldId && !!required;
    });
  }, [issueTypeEntitiesMap]);
};

/**
 * The useEffect hook changes the issue's status if there are required fields that are unfilled.
 * Once the hideStatusField and hiddenStandardFields props are removed from the IssueCreationForm,
 * we can remove this hook.
 */
exports.useGetIsFieldRequiredByIssueTypeId = useGetIsFieldRequiredByIssueTypeId;
const useChangeStatusWhenRequiredFieldsAreHidden = _ref7 => {
  let {
    hideStatusField,
    areThereHiddenRequiredStandardFields,
    issueFormValues,
    selectedType,
    setFieldValue
  } = _ref7;
  const formValuesStr = JSON.stringify(issueFormValues);
  (0, _react.useEffect)(() => {
    if (hideStatusField || areThereHiddenRequiredStandardFields) {
      const {
        customAttributes,
        ...restOfIssue
      } = issueFormValues;
      let customAttrArray = [];
      if (customAttributes) {
        customAttrArray = (0, _issueFieldSanitization.convertCustomAttrObjToArray)(customAttributes);
      }
      const areAllRequiredFieldsFilled = (0, _issueRequiredFields.getAreAllRequiredFieldsFilled)({
        fieldsMetadata: (selectedType === null || selectedType === void 0 ? void 0 : selectedType.fieldsMetadata) || [],
        issue: {
          ...restOfIssue,
          customAttributes: customAttrArray
        }
      });
      if (!areAllRequiredFieldsFilled && issueFormValues.status !== _sdk.IssueStatus.Draft) {
        setFieldValue("status", _sdk.IssueStatus.Draft);
      }
      if (areAllRequiredFieldsFilled && issueFormValues.status === _sdk.IssueStatus.Draft) {
        setFieldValue("status", _sdk.IssueStatus.Open);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areThereHiddenRequiredStandardFields, hideStatusField, setFieldValue, formValuesStr]);
};
exports.useChangeStatusWhenRequiredFieldsAreHidden = useChangeStatusWhenRequiredFieldsAreHidden;
//# sourceMappingURL=requiredFieldsByTypeHooks.js.map